import React from 'react';
import GlassPanel from '@/components/ui/GlassPanel';
import MonthlyRails from '@/components/ui/MonthlyRails';
import PlanetChart from '@/components/charts/PlanetChart';
import BeamProgress from '@/components/ui/BeamProgress';
import { initialEnvelopes } from '@/data/mock';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';

const { FiActivity, FiArrowUpRight, FiArrowDownRight } = FiIcons;

export default function Dashboard() {
  return (
    <div className="space-y-6 pb-24 md:pb-8 animate-in fade-in duration-500">
      
      {/* Header Area */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
         <div>
           <div className="flex items-center gap-2 mb-2">
             <div className="w-2 h-2 rounded-full bg-bb-accent shadow-[0_0_8px_rgba(0,240,255,0.8)] animate-pulse"></div>
             <p className="text-bb-accent font-mono text-[10px] tracking-widest uppercase">System Status: Nominal</p>
           </div>
           <h1 className="text-3xl font-bold text-white tracking-tight">Main Cockpit</h1>
         </div>
         <div className="text-right hidden md:block">
           <p className="text-xs text-gray-500 font-mono">CURRENT CYCLE</p>
           <p className="text-sm text-gray-300">October 2023</p>
         </div>
      </div>

      {/* Main Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         <GlassPanel className="p-6 md:col-span-2 relative overflow-hidden group">
            {/* Ambient Background Glow */}
            <div className="absolute -right-20 -top-20 w-64 h-64 bg-bb-gold/5 rounded-full blur-3xl group-hover:bg-bb-gold/10 transition-all duration-700"></div>
            
            <div className="flex justify-between items-start mb-4 relative z-10">
              <p className="text-xs text-gray-400 font-mono tracking-widest uppercase">Available Liquidity</p>
              <div className="flex gap-2">
                <span className="flex items-center text-xs font-mono text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded">
                  <SafeIcon icon={FiArrowDownRight} className="mr-1" /> +$3.2k
                </span>
                <span className="flex items-center text-xs font-mono text-bb-gold bg-bb-gold/10 px-2 py-1 rounded">
                  <SafeIcon icon={FiArrowUpRight} className="mr-1" /> -$1.1k
                </span>
              </div>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-mono text-white tracking-tighter flex items-center relative z-10">
              <span className="text-bb-gold mr-2 text-4xl opacity-80">$</span>
              12,450
              <span className="text-2xl text-gray-600 ml-1">.00</span>
            </h2>
         </GlassPanel>
         
         <GlassPanel className="p-6 flex flex-col justify-between">
            <div>
              <p className="text-xs text-gray-400 font-mono tracking-widest uppercase mb-2 flex items-center">
                <SafeIcon icon={FiActivity} className="mr-2 text-bb-accent" /> Burn Rate
              </p>
              <h3 className="text-3xl font-mono text-white">42.8%</h3>
            </div>
            
            <div className="mt-6">
              <div className="flex justify-between text-xs text-gray-500 mb-2 font-mono">
                <span>0%</span>
                <span className="text-bb-accent">OPTIMAL</span>
                <span>100%</span>
              </div>
              <div className="w-full h-1.5 bg-bb-dark rounded-full overflow-hidden border border-white/5">
                 <div className="w-[42.8%] h-full bg-bb-accent shadow-[0_0_10px_rgba(0,240,255,0.6)] rounded-full"></div>
              </div>
            </div>
         </GlassPanel>
      </div>

      <MonthlyRails />

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
         <GlassPanel className="p-6 flex flex-col">
            <h3 className="text-xs text-bb-accent font-mono uppercase tracking-[0.2em] mb-6 border-b border-bb-glassBorder pb-3">Sector Allocation Planet</h3>
            <div className="flex-1 min-h-[280px] relative">
               {/* Center Decal for the donut chart */}
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
                 <p className="text-[10px] text-gray-500 font-mono mb-1">TOTAL SPENT</p>
                 <p className="text-xl text-white font-mono">$1,105</p>
               </div>
               <PlanetChart />
            </div>
         </GlassPanel>

         <GlassPanel className="p-6">
            <div className="flex justify-between items-center mb-6 border-b border-bb-glassBorder pb-3">
               <h3 className="text-xs text-bb-accent font-mono uppercase tracking-[0.2em]">Active Envelope Beams</h3>
               <button className="text-gray-500 hover:text-bb-gold transition-colors text-[10px] font-mono uppercase border border-gray-700 hover:border-bb-gold px-2 py-1 rounded">View All</button>
            </div>
            <div className="space-y-2">
               {initialEnvelopes.slice(0,4).map(env => (
                 <BeamProgress key={env.id} {...env} />
               ))}
            </div>
         </GlassPanel>
      </div>
    </div>
  );
}